import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {brandT} from '@/lib/types/brand.types';
import {statsRowT} from '@/lib/types/stats.types';

import {renderBrandTableStats} from '@/lib/config/brandTableConfig';
import {createDataTableConfig} from '@/lib/helpers/config';

import {useDistributor} from '@/pages/(shared)/distributor/detail/providers/distributorProvider';

import {useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {ConfirmDeleteModalComponent} from '@/components/confirmDeleteModalComponent';
import {DataTable, DataTableRefT} from '@/components/dataTable';
import {SearchAndSelectComponent} from '@/components/searchAndSelectComponent';
import {StatsRowComponent} from '@/components/statsRowComponent';

export const DistributorDetailBrands = () => {
  const {isAdmin} = useAuth();
  const {toast} = useNotifications();
  const {distributorId, searchBrandsToAdd, addBrandToDistributor, removeBrandFromDistributor} = useDistributor();
  const {data: stats} = useViewQuery({view: 'brand_stats'});

  // State
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [searchModalVisible, setSearchModalVisible] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<brandT[] | []>([]);

  // Refs
  const dataTableRef = useRef<DataTableRefT>(null);
  const dataRowToDeleteRef = useRef<brandT | null>(null);

  // Data Table Config
  const dataTableConfig = createDataTableConfig({
    enabled: !!distributorId,
    table: 'Distributor_Brand',
    title: 'Brands Summary',
    columns: ['fk_brand_id', 'date_removed', ['Brand', ['brand_id', 'brand_name', 'brand_city']]],
    searchColumn: 'Brand.brand_name',
    filters: [{column: 'fk_distributor_id', operator: 'eq', value: distributorId}],
    addButtonLabel: 'Add Brand To Distributor',
    noResultsMessage: 'No brands associated with distributor',
    preventScrolling: true,
    onAdd: () => setSearchModalVisible(true),
    columnsDisplay: [
      {header: 'ID', field: 'Brand.brand_id'},
      {
        header: 'Status',
        field: 'date_created',
        render: row => (
          <span className={`badge bg-${!row.date_removed ? 'success' : 'danger'} text-white`}>
            {!row.date_removed ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        header: 'Name',
        field: 'Brand.brand_name',
        render: row => (
          <Link to={`/brands/${row.Brand.brand_id}`} className="text-primary hover:text-primary dark:text-primary">
            {row.Brand.brand_name}
          </Link>
        ),
      },
      {header: 'City', field: 'Brand.brand_city'},
    ],
    actionsDisplay: [
      {
        icon: 'ri-delete-bin-line',
        color: 'danger',
        handler: async row => {
          dataRowToDeleteRef.current = row.Brand;
          setDeleteModalVisible(true);
        },
        permission: () => isAdmin,
      },
    ],
  });

  // Stats
  const statsRow: statsRowT = renderBrandTableStats(stats?.total, stats?.active, stats?.inactive);

  // Functions
  // Function used to close the search modal
  const handleCloseSearchModal = () => {
    setSearchModalVisible(false);
    setSearchResults([]);
  };

  // Function used to get the search results
  const onSearch = async (query: string) => {
    const results = await searchBrandsToAdd(query);
    setSearchResults(results);
  };

  // Function used to add a row
  const handleAdd = async (brand: brandT) => {
    try {
      await addBrandToDistributor(brand.brand_id);
      toast.success({
        title: 'Brand added to Distributor',
        message: `Brand ${brand.brand_name} added to Distributor`,
      });
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Adding Brand To Distributor',
        message: (error as Error).message,
      });
    } finally {
      setSearchResults([]);
      setSearchModalVisible(false);
    }
  };

  // Function used to delete a row
  const onDeleteConfirm = async () => {
    if (!dataRowToDeleteRef.current) throw new Error('Brand not found');

    try {
      await removeBrandFromDistributor(dataRowToDeleteRef.current.brand_id);
      toast.success({
        title: 'Brand Removed From Distributor',
        message: `Brand ${dataRowToDeleteRef.current.brand_name} removed from Distributor`,
      });
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Removing Brand From Distributor',
        message: (error as Error).message,
      });
    } finally {
      setDeleteModalVisible(false);
      dataRowToDeleteRef.current = null;
      dataTableRef.current?.refetch();
    }
  };

  return (
    <>
      <StatsRowComponent stats={statsRow} />
      <DataTable ref={dataTableRef} {...dataTableConfig} />
      <SearchAndSelectComponent
        title="Search and Select Brands"
        isVisible={searchModalVisible}
        onClose={handleCloseSearchModal}
        onSelect={handleAdd}
        onSearch={onSearch}
        results={searchResults}
        mapStructure={{
          displayFields: [{title: 'Brand Name', field: 'brand_name'}],
          valueField: 'brand_id',
        }}
      />
      <ConfirmDeleteModalComponent
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        title="Confirm Deletion"
        description="Are you sure you want to remove this brand from the distributor?"
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};
